import { FilterOutlined } from '@ant-design/icons'
import { Button, Input, Spin, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { Select } from 'antd/lib'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { theme } from '@styles'

import { useGeneral, useUser } from '../../contexts'
import { useFiltersV2 } from '../../contexts/FiltersContextV2'
import { useCarriers, useEquipment, useUserResponseManager } from '../../hooks'
import useTrailersView from '../../hooks/useTrailersView'
import { Equipment } from '../../types'
import { ContentContainer, StyledRow, StyledSelect } from '../StyledComponents'

const { Option } = Select

const TrailersView: FC = () => {
  const { t } = useTranslation()
  const { state: user } = useUser()
  const { filters, handleFilterChange } = useFiltersV2()
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { allCarriersOptions } = useCarriers()
  const { trailers, isLoading } = useTrailersView({ filters })
  const { userIsLoading } = useUserResponseManager(user?.id)
  const { equipmentOptions } = useEquipment()

  const rowClassName = (_record: unknown, index: number) =>
    index % 2 === 0 ? 'zebra-even' : 'zebra-odd'

  const columnsTrailer: ColumnsType = [
    {
      title: t('tpl_dashboard.carrier'),
      dataIndex: 'carrier',
      key: 'carrier',
      render: ({ businessName }) => businessName,
    },
    {
      title: t('tpl_dashboard.licensePlate'),
      dataIndex: 'licensePlate',
      key: 'licensePlate',
    },
    {
      title: t('tpl_dashboard.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('tpl_dashboard.equipment'),
      dataIndex: 'equipmentType',
      key: 'equipmentType',
      render: (equipment: Equipment) => (
        <Tag
          style={{ color: theme.textColor }}
          color={equipment ? `#${equipment.color}` : theme.white}
        >
          {equipment ? equipment.nameShort : 'Unidad Tractora'}
        </Tag>
      ),
    },
    {
      title: t('tpl_dashboard.status'),
      dataIndex: 'disableAt',
      key: 'disableAt',
      render: (disableAt: boolean) =>
        !disableAt ? <span>HABILITADO</span> : <span style={{ color: 'red' }}>DESHABILITADO</span>,
    },
  ]

  return (
    <ContentContainer>
      <StyledRow className="sticky-options">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              value={
                filters.carrierId
                  ? (filters.carrierId as number[]).map(value => Number(value))
                  : undefined
              }
              mode="multiple"
              placeholder={t('tpl_dashboard.carrier')}
              onChange={handleFilterChange('carrierId')}
              options={allCarriersOptions?.map(option => ({
                label: option.label,
                value: option.value,
              }))}
            />
            <StyledSelect
              maxTagCount="responsive"
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder={t('tpl_dashboard.equipment')}
              onChange={handleFilterChange('equipmentId')}
            >
              {equipmentOptions &&
                equipmentOptions
                  .filter(equipmentType => equipmentType.straightTruck === false)
                  .map(equipmentType => (
                    <Option key={equipmentType.value} value={equipmentType.value}>
                      {equipmentType.label}
                    </Option>
                  ))}
            </StyledSelect>
            <Input
              style={{ flex: 1 }}
              placeholder={t('tpl_router.tpl_dashboard.edit_truck_modal.licensePlate')}
              type="string"
              onPressEnter={e => {
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('licensePlate')(undefined)
                } else {
                  handleFilterChange('licensePlate')(e.currentTarget.value)
                }
              }}
              onBlur={e => {
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('licensePlate')(undefined)
                } else {
                  handleFilterChange('licensePlate')(e.currentTarget.value)
                }
              }}
              onChange={e => {
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('licensePlate')(undefined)
                }
              }}
              allowClear
            />
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              value={filters.disabled}
              placeholder={t('tpl_dashboard.state')}
              onChange={handleFilterChange('disabled')}
            >
              <Option value={false}>{t('globals.generics.enabled')}</Option>
              <Option value={true}>{t('globals.generics.disabled')}</Option>
            </StyledSelect>
          </div>
        </div>
      </StyledRow>
      <StyledRow className="ticket-handler">
        <div style={{ flex: 1 }} />
        {userIsLoading ? <Spin /> : <div style={{ flex: 1 }} />}
      </StyledRow>
      <Table
        columns={columnsTrailer}
        dataSource={trailers?.trailers}
        loading={isLoading}
        rowKey="id"
        rowClassName={rowClassName}
        pagination={{
          pageSize: filters.limit as number,
          onChange: handleFilterChange('page', false),
          total: trailers?.total,
          showSizeChanger: false,
          current: filters.page as number,
        }}
      />
    </ContentContainer>
  )
}

export default TrailersView
